import type { Copy } from "../types";

export const fi: Copy = {
    seo: {
      title: "Auton iän laskuri | Tarkista ajoneuvon ikä vuoden ja kuukauden mukaan",
      description:
        "Tarkista ajoneuvon ikä valmistusvuoden ja valinnaisen kuukauden perusteella. Näe vuodet, kuukaudet, päivät, kokonaispäivät ja merkkipaalut.",
      keywords: [
        "auton iän laskuri",
        "ajoneuvon iän laskuri",
        "auton valmistusvuosi",
        "ajoneuvon valmistuskuukausi",
        "kuinka vanha autoni on",
        "auton ikä vuoden mukaan",
        "tarkista ajoneuvon ikä",
        "RC iän laskuri",
        "auton ikä kuukauden mukaan",
        "ajoneuvon päivämäärälaskuri",
        "mParivahan vaihtoehto",
        "Parivahan vaihtoehto",
        "RTO ajoneuvon iän laskuri",
        "rekisteröinti-iän tarkistus",
        "ajoneuvon rekisteröinti-ikä",
      ],
      faq: [
        {
          question: "Mitä tämä laskuri tekee?",
          answer:
            "Se laskee ajoneuvon tarkan iän valitun valmistusvuoden ja valinnaisen kuukauden perusteella ja näyttää vuodet, kuukaudet, päivät ja merkkipaalut.",
        },
        {
          question: "Miksi kuukausi on valinnainen?",
          answer:
            "Kuukausi tekee iän laskennasta tarkemman. Jos jätät sen pois, sovellus käyttää valitun vuoden tammikuuta.",
        },
        {
          question: "Voinko valita tulevan vuoden tai kuukauden?",
          answer:
            "Et. Tulevat vuodet estetään, ja tulevat kuukaudet poistetaan käytöstä, kun valittu vuosi on nykyinen vuosi.",
        },
      ],
    },
    headerTagline: "Ajoneuvotyökalu",
    headerTitle: "Auton iän laskuri",
    reset: "Nollaa",
    switchToLight: "Vaihda vaaleaan tilaan",
    switchToDark: "Vaihda tummaan tilaan",
    languageLabel: "Kieli",
    languageEnglish: "Englanti",
    languageHindi: "Hindi",
    heroEyebrow: "Auton ikä selkeästi",
    heroTitle: "Selkeä päivämäärätyökalu ajoneuvon iälle.",
    heroDescription:
      "Valitse valmistusvuosi. Lisää kuukausi vain, jos haluat tarkemman ikätuloksen.",
    yearSelectLabel: "Valmistusvuosi",
    monthSelectLabel: "Valmistuskuukausi",
    includeMonthLabel: "Lisää tarkka kuukausi parempaa tarkkuutta varten",
    includeMonthNote:
      "Jos et lisää kuukautta, laskennassa käytetään tammikuuta.",
    yearOnlyBadge: "Vain vuosi",
    yearMonthBadge: "Vuosi + kuukausi",
    yearPlaceholder: "Valitse vuosi",
    monthPlaceholder: "Valitse kuukausi",
    inputTitle: "Valmistusvuosi",
    inputLabel: "Valmistusvuosi",
    blockedDateNote: "Tulevat vuodet on estetty.",
    result: "Tulos",
    vehicleAgeLead: "Ajoneuvosi on",
    asOfToday: "Tänään",
    years: "Vuotta",
    months: "Kuukautta",
    days: "Päivää",
    totalDays: (count) => `${count.toLocaleString("fi-FI")} päivää yhteensä`,
    ageLabels: {
      almostNew: "Lähes uusi",
      modern: "Moderni",
      mature: "Vakiintunut",
      classic: "Klassikko",
    },
    estimatedValue: {
      high: "Korkea jälleenmyyntipotentiaali",
      healthy: "Hyvä arvon säilyminen",
      moderate: "Kohtalainen arvon aleneminen",
      collector: "Keräilykiinnostus voi kasvaa",
    },
    maintenanceTitle: "Huolto",
    maintenance: {
      routine: "Rutiinihuolto ja OEM-ylläpito",
      watch: "Seuraa jarruja, nesteitä ja renkaita",
      plan: "Suunnittele kuluvat osat ja tarkastukset",
      specialized: "Erikoishuollosta tulee arvokasta",
    },
    registrationTitle: "Rekisteröinti",
    registration: {
      collector: "Voi joillakin alueilla sopia keräilyajoneuvon kohteluun",
      classic: "Klassikkoauton rekisteröinti voi olla mahdollinen joillakin alueilla",
      standard: "Odota tavallisia uusintoja ja alueellisia tarkastuksia",
      regular: "Tavallinen rekisteröinti yleensä pätee",
    },
    upcomingMilestoneTitle: "Seuraava merkkipaalu",
    milestoneTimelineTitle: "Merkkipaalujen aikajana",
    milestoneRemaining: ({ label, days }) => `${label} ${days.toLocaleString("fi-FI")} päivän päästä`,
    reached: "Saavutettu",
    upcoming: "Tulossa",
    allMilestonesReachedTitle: "Kaikki listatut merkkipaalut saavutettu",
    allMilestonesReachedBody:
      "Tämä ajoneuvo on ohittanut sovelluksessa tällä hetkellä näytettävän merkkipaaluvälin.",
    copySummary: "Kopioi yhteenveto",
    shareResult: "Jaa tulos",
    resetMessage: "Laskuri nollattu",
    summaryCopied: "Yhteenveto kopioitu leikepöydälle",
    clipboardFailed: "Leikepöydän käyttö epäonnistui",
    emptyTitle: "Valitse valmistusvuosi nähdäksesi koko erittelyn",
    emptyDescription:
      "Kun vuosi on valittu, näet tarkan iän, kokonaispäivät, huoltotiedot, klassikkostatuksen ja merkkipaalut.",
    shareTitle: "Auton iän laskuri",
    summary: ({ label, years, months, days, asOf }) =>
      `Autoni valmistettiin ${label}, ja ${asOf} mennessä se on ${years} vuotta, ${months} kuukautta ja ${days} päivää vanha.`,
    milestones: {
      1: { label: "Ensimmäinen vuosi", note: "Hyödyllinen takuun ja huollon suunnitteluun." },
      3: { label: "Kolme vuotta", note: "Yleinen kohta jälleenmyynnille ja arvon muutoksille." },
      5: { label: "Viisi vuotta", note: "Hyvä hetki tarkempaan huoltobudjetointiin." },
      10: { label: "Kymmenen vuotta", note: "Ikääntyvät osat tarvitsevat lisähuomiota." },
      15: { label: "Viisitoista vuotta", note: "Monilla alueilla klassikkokohtelu voi alkaa tässä vaiheessa." },
      25: { label: "Kaksikymmentäviisi vuotta", note: "Keräily- ja tuontikiinnostus voi kasvaa joillakin markkinoilla." },
    },
  };
